/**
 * GTM Surface Layer — GA4 Standard E-Commerce Formatters
 *
 * Formats events for window.dataLayer using GA4 e-commerce schema.
 * All items include item_brand (venue name) for multi-brand enterprise tracking.
 */

import { VenueContext } from './types';

interface GA4Item {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_category: string;
  price?: number;
  quantity?: number;
  item_list_id?: string;
  item_list_name?: string;
  index?: number;
}

function buildGA4Item(
  venue: VenueContext,
  item: { id: string; name: string; category: string; price?: number; quantity?: number },
  extra?: { listId?: string; listName?: string; index?: number }
): GA4Item {
  const ga4Item: GA4Item = {
    item_id: item.id,
    item_name: item.name,
    item_brand: venue.venueName,
    item_category: item.category,
    ...(item.price !== undefined && { price: item.price }),
    ...(item.quantity !== undefined ? { quantity: item.quantity } : item.price !== undefined && { quantity: 1 })
  };
  if (extra?.listId) {
    ga4Item.item_list_id = extra.listId;
  }
  if (extra?.listName) {
    ga4Item.item_list_name = extra.listName;
  }
  if (extra?.index !== undefined) {
    ga4Item.index = extra.index;
  }
  return ga4Item;
}

// --- Formatters ---

export function formatGA4PageView(params: { path: string; title: string }) {
  return {
    event: 'page_view',
    page_location: typeof window !== 'undefined' ? window.location.href : '',
    page_title: params.title,
    page_path: params.path,
    page_referrer: typeof document !== 'undefined' ? document.referrer : ''
  };
}

export function formatGA4Search(venue: VenueContext, params: { partySize: number; date?: string; time?: string; resultsCount: number }) {
  return {
    event: 'search',
    search_term: `party_size:${params.partySize}`,
    currency: venue.currency,
    items_count: params.resultsCount
  };
}

export function formatGA4ViewItemList(
  venue: VenueContext,
  items: { id: string; name: string; category: string }[],
  listId?: string,
  listName?: string
) {
  return {
    event: 'view_item_list',
    ecommerce: {
      item_list_id: listId || 'table_results',
      item_list_name: listName || 'Table Results',
      items: items.map((item, index) =>
        buildGA4Item(venue, item, { listId, listName, index })
      )
    }
  };
}

export function formatGA4ViewItem(
  venue: VenueContext,
  params: { itemId: string; itemName: string; itemCategory: string; price?: number }
) {
  const ecommerce: Record<string, unknown> = {
    currency: venue.currency,
    items: [
      buildGA4Item(venue, {
        id: params.itemId,
        name: params.itemName,
        category: params.itemCategory,
        price: params.price
      })
    ]
  };
  if (params.price !== undefined) {
    ecommerce.value = params.price;
  }
  return {
    event: 'view_item',
    ecommerce
  };
}

export function formatGA4SelectItem(
  venue: VenueContext,
  params: { itemId: string; itemName: string; itemCategory: string; price?: number }
) {
  return {
    event: 'select_item',
    ecommerce: {
      currency: venue.currency,
      items: [
        buildGA4Item(venue, {
          id: params.itemId,
          name: params.itemName,
          category: params.itemCategory,
          price: params.price
        })
      ]
    }
  };
}

export function formatGA4AddToCart(
  venue: VenueContext,
  params: { items: { id: string; name: string; price: number; quantity: number }[] }
) {
  const value = params.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  return {
    event: 'add_to_cart',
    ecommerce: {
      currency: venue.currency,
      value,
      items: params.items.map((item) =>
        buildGA4Item(venue, { ...item, category: 'Add-on' })
      )
    }
  };
}

export function formatGA4BeginCheckout(
  venue: VenueContext,
  params: { value: number; items: { id: string; name: string; price: number; quantity: number }[] }
) {
  return {
    event: 'begin_checkout',
    ecommerce: {
      currency: venue.currency,
      value: params.value,
      items: params.items.map((item) =>
        buildGA4Item(venue, { ...item, category: 'Reservation' })
      )
    }
  };
}

export function formatGA4AddPaymentInfo(
  venue: VenueContext,
  params: { value: number }
) {
  return {
    event: 'add_payment_info',
    ecommerce: {
      currency: venue.currency,
      value: params.value
    }
  };
}

export function formatGA4Purchase(
  venue: VenueContext,
  params: {
    transactionId: string;
    value: number;
    tax?: number;
    items: { id: string; name: string; category: string; price: number; quantity: number }[];
  }
) {
  return {
    event: 'purchase',
    ecommerce: {
      transaction_id: params.transactionId,
      currency: venue.currency,
      value: params.value,
      ...(params.tax !== undefined && { tax: params.tax }),
      items: params.items.map((item) => buildGA4Item(venue, item))
    }
  };
}
